import * as React from 'react';
import * as _ from 'underscore';

import { MetricDetailView } from '@tableau/metrics-shared-widgets-dom/lib/src/MetricDetailView';
import { metricDetailPropsFromMetric } from '../../../metrics/metricCardPropsFromSummary';
import { SpaceContainerStyles } from '../../../style/SpaceContainerStyles';
import { Metrics } from '../../../ts/resources/Metrics';
import { IMetricWithSummaryData } from '../../../ts/ServerApi/types/IMetricWithSummaryData';
import { ServiceLocator } from '../../ServiceLocator';
import { SpaceInfo } from '../../state/SpaceInfo';
import { RouterError } from '../RouterHelpers/RouterError';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';

export interface MetricDetailSpaceOwnProps {
  readonly luid: string;
  readonly urls: SpacesSiteUrls;
}
export interface MetricDetailSpaceState {
  readonly metric: IMetricWithSummaryData;
  readonly hasError: boolean;
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
}

const InitialState: MetricDetailSpaceState = {
  metric: null,
  hasError: false,
  initialRender: true,
  renderChildren: false,
};

export class MetricDetailSpace extends React.PureComponent<MetricDetailSpaceOwnProps, MetricDetailSpaceState> {
  private unmounted: boolean = false;
  private spaceInfo: SpaceInfo = undefined;

  public constructor(props: MetricDetailSpaceOwnProps){
    super(props);
    this.state = InitialState;
  }

  public componentDidMount() {
    this.getMetricForLuid();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate(prevProps: MetricDetailSpaceOwnProps) {
    if (this.changedMetric(prevProps)) {
      this.updateState(InitialState /* want the initial state for the metric */);
      this.getMetricForLuid();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender && this.state.metric) {
      this.setState({initialRender: false, renderChildren: true});
    }
  }

  private updateState<K extends keyof MetricDetailSpaceState>(newState: Pick<MetricDetailSpaceState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }

  private changedMetric(prevProps: MetricDetailSpaceOwnProps) {
    return !_.isUndefined(prevProps.luid)
      && !_.isUndefined(this.props.luid)
      && prevProps.luid !== this.props.luid;
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.metric);
  }

  private getMetricForLuid = async () => {
      try {
        const metricsResource = ServiceLocator.get<Metrics>('Metrics');
        const metrics = await metricsResource.getMetricsByLuids([this.props.luid]);
        if (metrics.metrics && metrics.metrics.length >= 1) {
            this.spaceInfo = {item: {...metrics.metrics[0], contentType: 'metric', id: this.props.luid}, spaceType: 'metric', subspaceName: undefined };
            this.updateState({metric: metrics.metrics[0]});
        } else {
            this.updateState({hasError: true});
        }
      } catch (e) {
        this.updateState({hasError: true});
      }
  }

  public render() {
    if (this.state.renderChildren) {
        return (
          <ConnectedSpaceInfoProvider spaceInfo={this.spaceInfo}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
              <MetricDetailView
                {...metricDetailPropsFromMetric(this.state.metric)}
                style={{
                  lineHeight: 'normal',
                  flex: 1,
                }}
              />
            </div>
          </ConnectedSpaceInfoProvider>);
    } else if (this.state.hasError) {
        return <RouterError errorType={'metricDetail'} urls={this.props.urls} />;
    } else {
        return null;
    }
  }
}
